import { Controller } from '@hotwired/stimulus'
import { i18n } from '@lingui/core'
import { plural, t } from '@lingui/core/macro'
import { I18nProvider } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import { createRoot } from 'react-dom/client'

export default class extends Controller {
  static targets = ['count', 'toggleable']
  static values = { resourceType: String }

  connect() {
    this.updateCount()
    this.addEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
  }

  updateCount() {
    if (!this.hasCountTarget) return

    // Count all checked checkboxes with the toggle-target attribute
    const checkedBoxes = this.element.querySelectorAll(
      'input[type="checkbox"]:checked[data-toggle-target="toggleable"]',
    )
    const countTargets = this.element.querySelectorAll('[data-optimization-form-target="count"]')
    countTargets.forEach((target) => {
      target.textContent = checkedBoxes.length
    })
  }

  addEventListeners() {
    this.checkboxes = this.element.querySelectorAll('input[type="checkbox"]')
    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', this.updateCount.bind(this))
    })

    const selectedExecutionContext = this.element.querySelector(
      'input[name="ec2_optimizations[execution_context]"]:checked',
    )
    if (selectedExecutionContext) {
      this.toggleChangeRequestFields({ target: selectedExecutionContext })
    }
  }

  removeEventListeners() {
    if (this.checkboxes) {
      this.checkboxes.forEach((checkbox) => {
        checkbox.removeEventListener('change', this.updateCount.bind(this))
      })
    }
  }

  // Toggle functionality for "select all" checkboxes
  toggle(event) {
    const group = event.target.dataset.group
    const isChecked = event.target.checked

    // Find all checkboxes in the same group
    const groupCheckboxes = this.element.querySelectorAll(`input[type="checkbox"][data-group="${group}"]`)
    groupCheckboxes.forEach((checkbox) => {
      checkbox.checked = isChecked
    })
    this.updateCount()
  }

  handleSubmit() {
    this.showConfirmationModal()
  }

  showEbsOptimizationWizardStep(event) {
    const step = Number(event?.currentTarget?.dataset?.ebsWizardStep)
    if (!step) return

    const step1 = document.getElementById('ebs-optimization-wizard-step-1')
    const step2 = document.getElementById('ebs-optimization-wizard-step-2')
    const step1Dot = document.getElementById('ebs-optimization-wizard-step-1-dot')
    const step2Dot = document.getElementById('ebs-optimization-wizard-step-2-dot')

    if (!step1 || !step2 || !step1Dot || !step2Dot) return

    const isStep2 = step === 2
    step1.classList.toggle('d-none', isStep2)
    step2.classList.toggle('d-none', !isStep2)

    if (isStep2) {
      step1Dot.classList.remove('bg-primary', 'text-white', 'border-primary')
      step1Dot.classList.add('bg-success', 'border-success', 'text-white')

      step2Dot.classList.remove('bg-light', 'text-muted', 'border-secondary')
      step2Dot.classList.add('bg-primary', 'text-white', 'border-primary')
    } else {
      step1Dot.classList.remove('bg-success', 'border-success')
      step1Dot.classList.add('bg-primary', 'text-white', 'border-primary')

      step2Dot.classList.add('bg-light', 'text-muted', 'border-secondary')
      step2Dot.classList.remove('bg-primary', 'text-white', 'border-primary')
    }
  }

  toggleChangeRequestFields(event) {
    const value = event?.target?.value
    const changeRequestSection = this.element.querySelector('[data-change-request-fields]')
    if (!changeRequestSection) return

    const enabled = value === 'change_request'
    changeRequestSection.classList.toggle('d-none', !enabled)
    const fields = changeRequestSection.querySelectorAll('input, select, textarea')
    fields.forEach((field) => {
      field.required = enabled
    })
  }

  showConfirmationModal() {
    const selectedCount = this.getSelectedCount()

    if (selectedCount === 0) {
      alert(t`Please select at least one ${this.resourceName()} to optimize.`)
      return
    }

    const modalContainer = document.createElement('div')
    modalContainer.id = 'confirmation-modal-container'
    document.body.appendChild(modalContainer)

    const root = createRoot(modalContainer)

    const ConfirmationModal = () => {
      const [show, setShow] = React.useState(true)

      const handleConfirm = () => {
        setShow(false)
        const form = this.element.querySelector('form')
        if (form) {
          form.submit()
        }
        setTimeout(() => {
          document.body.removeChild(modalContainer)
        }, 100)
      }

      const handleCancel = () => {
        setShow(false)
        setTimeout(() => {
          document.body.removeChild(modalContainer)
        }, 100)
      }

      return (
        <Modal show={show} onHide={handleCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <Trans>Confirm {this.resourceName()} Optimization</Trans>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              <Trans>
                You are about to schedule optimization actions for{' '}
                <strong>{this.resourceCountLabel(selectedCount)}</strong>.
              </Trans>
            </p>
            <p className='mb-0'>
              <Trans>This will apply changes to your AWS infrastructure. Do you want to continue?</Trans>
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={handleCancel}>
              <Trans>Cancel</Trans>
            </Button>
            <Button variant='primary' onClick={handleConfirm}>
              <Trans>Confirm and Schedule</Trans>
            </Button>
          </Modal.Footer>
        </Modal>
      )
    }

    root.render(
      <I18nProvider i18n={i18n}>
        <ConfirmationModal />
      </I18nProvider>,
    )
  }

  resourceName() {
    const type = this.resourceTypeValue || ''
    if (type === 'RDS') return t`RDS Instance`
    if (type === 'EBS') return t`Volume`
    if (type === 'SavingsPlan') return t`Savings Plan recommendation`
    return t`Resource`
  }

  resourceCountLabel(count) {
    const type = this.resourceTypeValue || ''
    if (type === 'RDS') {
      return plural(count, {
        one: '# RDS Instance',
        other: '# RDS Instances',
      })
    }
    if (type === 'EBS') {
      return plural(count, {
        one: '# Volume',
        other: '# Volumes',
      })
    }
    if (type === 'SavingsPlan') {
      return plural(count, {
        one: '# Savings Plan recommendation',
        other: '# Savings Plan recommendations',
      })
    }
    return plural(count, {
      one: '# Resource',
      other: '# Resources',
    })
  }

  getSelectedCount() {
    const checkedBoxes = this.element.querySelectorAll(
      'input[type="checkbox"]:checked[data-toggle-target="toggleable"]',
    )
    return checkedBoxes.length
  }

  selectAll() {
    const volumeCheckboxes = this.element.querySelectorAll('input[type="checkbox"][data-toggle-target="toggleable"]')
    volumeCheckboxes.forEach((checkbox) => {
      checkbox.checked = true
    })

    this.updateSelectAllCheckboxes(true)
    this.updateCount()
  }

  unselectAll() {
    const volumeCheckboxes = this.element.querySelectorAll('input[type="checkbox"][data-toggle-target="toggleable"]')
    volumeCheckboxes.forEach((checkbox) => {
      checkbox.checked = false
    })

    this.updateSelectAllCheckboxes(false)
    this.updateCount()
  }

  updateSelectAllCheckboxes(checked) {
    // Update all "select all" checkboxes to reflect the current state
    const selectAllCheckboxes = this.element.querySelectorAll('input[type="checkbox"][id^="select_all_"]')
    selectAllCheckboxes.forEach((checkbox) => {
      checkbox.checked = checked
    })
  }

  selectAllCpu() {
    const cpuCheckboxes = this.element.querySelectorAll(
      'input[type="checkbox"][id^="ec2_cpu_optimized_recommendation_"][data-toggle-target="toggleable"]',
    )
    cpuCheckboxes.forEach((checkbox) => {
      checkbox.checked = true
    })
    this.updateCount()
  }

  unselectAllCpu() {
    const cpuCheckboxes = this.element.querySelectorAll(
      'input[type="checkbox"][id^="ec2_cpu_optimized_recommendation_"][data-toggle-target="toggleable"]',
    )
    cpuCheckboxes.forEach((checkbox) => {
      checkbox.checked = false
    })
    this.updateCount()
  }
}
